import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffAcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing invitation token.");
      return;
    }
    axios
      .get(`${API}/ops/staff/invitations/verify`, { params: { token } })
      .then((r) => {
        setInvitation(r.data);
        setName(r.data.name_hint || "");
      })
      .catch((e) =>
        setError(
          e?.response?.data?.detail ||
            "This invitation could not be verified."
        )
      );
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/ops/staff/invitations/accept`, {
        token,
        email: invitation.email,
        password,
        name,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login?next=/ops"), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to accept invitation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="staff-accept-page"
      className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6 py-10"
    >
      <div className="w-full max-w-md space-y-6">
        <Link
          to="/"
          className="text-xs uppercase tracking-widest text-slate-400 hover:text-slate-100"
        >
          ← Bonded Thru Cancer
        </Link>
        <h1
          data-testid="staff-accept-heading"
          className="text-3xl font-serif"
        >
          Accept your BTC Operations invitation
        </h1>
        {error && (
          <div
            data-testid="staff-accept-error"
            className="border border-rose-500/40 bg-rose-500/10 text-rose-100 text-sm px-4 py-3 rounded"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            data-testid="staff-accept-success"
            className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-100 text-sm px-4 py-3 rounded"
          >
            Account activated. Redirecting to sign in…
          </div>
        )}
        {invitation && !success && (
          <form
            onSubmit={submit}
            data-testid="staff-accept-form"
            className="space-y-4"
          >
            <div className="text-sm text-slate-300">
              <div>
                <span className="text-slate-500">Email:</span>{" "}
                <span
                  data-testid="staff-accept-email"
                  className="text-slate-100"
                >
                  {invitation.email}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Assigned role:</span>{" "}
                <span
                  data-testid="staff-accept-role"
                  className="text-slate-100"
                >
                  {invitation.role}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Roles cannot be changed during acceptance.
              </div>
            </div>
            <label className="block text-sm space-y-1">
              <span className="text-slate-400 text-xs uppercase tracking-widest">
                Name
              </span>
              <input
                data-testid="staff-accept-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                required
              />
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-slate-400 text-xs uppercase tracking-widest">
                Choose a password (min 8 chars)
              </span>
              <input
                data-testid="staff-accept-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
              />
            </label>
            <button
              data-testid="staff-accept-submit"
              disabled={busy}
              className="w-full bg-slate-100 text-slate-900 px-4 py-2 rounded text-sm font-medium hover:bg-white disabled:opacity-50"
            >
              {busy ? "Activating…" : "Activate my operator account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
